// AnimatedLogo.jsx - Logo animado e interactivo de MediExamPro
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const AnimatedLogo = ({ 
  size = 'medium', 
  animated = true, 
  showText = true,
  interactive = true,
  variant = 'full' // full, minimal, icon
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [clickCount, setClickCount] = useState(0);
  const [showEasterEgg, setShowEasterEgg] = useState(false);

  // Tamaños responsivos
  const sizes = {
    small: { width: 40, height: 40, text: 'text-sm' },
    medium: { width: 60, height: 60, text: 'text-base' },
    large: { width: 120, height: 120, text: 'text-xl' },
    hero: { width: 200, height: 200, text: 'text-3xl' }
  };

  const currentSize = sizes[size] || sizes.medium;

  // Easter egg: 5 clicks rápidos activan animación especial
  useEffect(() => {
    if (clickCount >= 5) {
      setShowEasterEgg(true);
      setTimeout(() => {
        setShowEasterEgg(false);
        setClickCount(0);
      }, 3000);
    }
  }, [clickCount]);

  // Reset click count después de 2 segundos
  useEffect(() => {
    const timer = setTimeout(() => {
      if (clickCount > 0 && clickCount < 5) {
        setClickCount(0);
      }
    }, 2000);
    return () => clearTimeout(timer);
  }, [clickCount]);

  // Variantes de animación para el logo
  const logoVariants = {
    initial: { 
      scale: 0,
      rotate: -180,
      opacity: 0
    },
    animate: { 
      scale: 1,
      rotate: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 260,
        damping: 20,
        duration: 1
      }
    },
    hover: {
      scale: 1.05,
      rotate: [0, -5, 5, -5, 0],
      transition: {
        rotate: {
          duration: 0.5,
          ease: "easeInOut"
        },
        scale: {
          duration: 0.3
        }
      }
    },
    tap: {
      scale: 0.95,
      rotate: 360,
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 10
      }
    },
    easterEgg: {
      rotate: [0, 360, 720, 1080],
      scale: [1, 1.2, 0.8, 1],
      transition: {
        duration: 2,
        times: [0, 0.3, 0.7, 1],
        ease: "easeInOut"
      }
    }
  };

  // Animación de partículas doradas flotantes
  const ParticleEffect = () => (
    <div className="absolute inset-0 pointer-events-none">
      {[...Array(6)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-1 h-1 bg-yellow-400 rounded-full"
          initial={{ 
            x: 0, 
            y: 0, 
            opacity: 0 
          }}
          animate={isHovered ? {
            x: [0, (i % 2 ? 30 : -30) * Math.random(), 0],
            y: [0, -40 * Math.random(), 0],
            opacity: [0, 1, 0]
          } : {}}
          transition={{
            duration: 2,
            delay: i * 0.1,
            repeat: Infinity,
            repeatDelay: 1
          }}
          style={{
            left: `${20 + i * 15}%`,
            top: `${30 + i * 10}%`
          }}
        />
      ))}
    </div>
  );

  // Efecto de brillo animado
  const ShimmerEffect = () => (
    <motion.div
      className="absolute inset-0 pointer-events-none"
      initial={{ opacity: 0 }}
      animate={isHovered ? { opacity: 1 } : { opacity: 0 }}
    >
      <motion.div
        className="absolute inset-0 bg-gradient-to-r from-transparent via-yellow-300/20 to-transparent"
        animate={{
          x: ['-100%', '200%']
        }}
        transition={{
          duration: 1.5,
          repeat: Infinity,
          repeatDelay: 2,
          ease: "easeInOut"
        }}
        style={{ transform: 'skewX(-20deg)' }}
      />
    </motion.div>
  );

  // Anillo pulsante
  const PulseRing = () => (
    <motion.div
      className="absolute inset-0 rounded-full border-2 border-yellow-400/50"
      animate={animated ? {
        scale: [1, 1.2, 1],
        opacity: [0.5, 0, 0.5]
      } : {}}
      transition={{
        duration: 2,
        repeat: Infinity,
        ease: "easeInOut"
      }}
    />
  );

  const handleClick = () => {
    if (interactive) {
      setClickCount(prev => prev + 1);
    }
  };

  return (
    <motion.div
      className={`relative inline-flex items-center gap-3 ${interactive ? 'cursor-pointer select-none' : ''}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleClick}
      initial="initial"
      animate={showEasterEgg ? "easterEgg" : "animate"}
      whileHover={interactive ? "hover" : undefined}
      whileTap={interactive ? "tap" : undefined}
    >
      {/* Contenedor del logo */}
      <div className="relative">
        {/* Efectos de fondo */}
        {animated && <PulseRing />}
        
        {/* Logo principal */}
        <motion.div
          className="relative overflow-hidden rounded-full"
          style={{ width: currentSize.width, height: currentSize.height }}
          variants={logoVariants}
        >
          {/* Imagen del logo */}
          <img
            src="/logo-mediexampro.jpeg"
            alt="MediExamPro"
            className="w-full h-full object-cover"
            draggable="false"
          />
          
          {/* Efectos superpuestos */}
          {animated && interactive && <ShimmerEffect />}
          
          {/* Overlay gradient para modo oscuro */}
          <div className="absolute inset-0 bg-gradient-to-t from-blue-900/20 to-transparent dark:from-blue-900/40 pointer-events-none" />
        </motion.div>

        {/* Partículas flotantes */}
        {animated && interactive && <ParticleEffect />}

        {/* Badge de notificación (ejemplo para eventos especiales) */}
        <AnimatePresence>
          {showEasterEgg && (
            <motion.div
              className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
            >
              ✨
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Texto del logo */}
      {showText && (
        <motion.div
          className="flex flex-col"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
        >
          <motion.span 
            className={`font-bold ${currentSize.text} bg-gradient-to-r from-yellow-400 to-yellow-600 bg-clip-text text-transparent`}
            animate={isHovered ? { 
              backgroundPosition: ['0%', '100%', '0%'] 
            } : {}}
            transition={{ duration: 2 }}
          >
            MediExamPro
          </motion.span>
          {size !== 'small' && (
            <motion.span 
              className="text-xs text-gray-500 dark:text-gray-400"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              Tu acceso al residentado
            </motion.span>
          )}
        </motion.div>
      )}

      {/* Tooltip con información */}
      <AnimatePresence>
        {isHovered && interactive && size === 'small' && (
          <motion.div
            className="absolute top-full mt-2 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-50"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            MediExamPro
            <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-gray-900 rotate-45" />
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Componente de Loading con el logo
export const LogoLoader = ({ message = "Cargando..." }) => {
  const [dots, setDots] = useState('');

  useEffect(() => {
    const interval = setInterval(() => {
      setDots(prev => prev.length >= 3 ? '' : prev + '.');
    }, 500);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="flex flex-col items-center justify-center p-8">
      <motion.div
        animate={{
          rotate: 360,
          scale: [1, 1.1, 1]
        }}
        transition={{
          rotate: {
            duration: 3,
            repeat: Infinity,
            ease: "linear"
          },
          scale: {
            duration: 1.5,
            repeat: Infinity,
            ease: "easeInOut"
          }
        }}
        className="mb-4"
      >
        <AnimatedLogo size="large" animated={true} showText={false} interactive={false} />
      </motion.div>
      <motion.p
        className="text-gray-600 dark:text-gray-400"
        animate={{ opacity: [0.5, 1, 0.5] }}
        transition={{ duration: 1.5, repeat: Infinity }}
      >
        {message}{dots}
      </motion.p>
    </div>
  );
};

// Componente de Success con el logo
export const LogoSuccess = ({ message = "¡Éxito!" }) => {
  return (
    <motion.div
      className="flex flex-col items-center justify-center p-8"
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{ type: "spring", stiffness: 200, damping: 10 }}
    >
      <motion.div
        animate={{
          rotate: [0, -10, 10, -10, 0],
          scale: [1, 1.2, 1]
        }}
        transition={{
          duration: 0.5,
          ease: "easeInOut"
        }}
      >
        <AnimatedLogo size="large" animated={true} showText={false} interactive={false} />
      </motion.div>
      <motion.div
        className="mt-4 text-2xl font-bold text-green-600"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        {message}
      </motion.div>
      
      {/* Confetti effect */}
      <div className="absolute inset-0 pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 bg-yellow-400"
            initial={{
              x: 0,
              y: 0,
              opacity: 1
            }}
            animate={{
              x: (Math.random() - 0.5) * 300,
              y: Math.random() * -200 - 100,
              opacity: 0,
              rotate: Math.random() * 360
            }}
            transition={{
              duration: 1,
              delay: i * 0.02,
              ease: "easeOut"
            }}
            style={{
              left: '50%',
              top: '50%',
              borderRadius: Math.random() > 0.5 ? '50%' : '0%'
            }}
          />
        ))}
      </div>
    </motion.div>
  );
};

export default AnimatedLogo;